import { z } from 'zod';
import { DynamicPipelineAnalysisConfiguration } from 'libs/end-to-end/src/dynamic-pipeline-analysis-configuration';
import { CustomVizRequest } from 'libs/end-to-end/src/custom-viz/CustomVizRequest';
import { guardExhaustive } from 'libs/end-to-end/src/generic';
import { FullCopilotDefinition } from 'libs/end-to-end/src/types/databases/copilots/copilot-definition';
import { ByMetricTrendsConfig } from 'libs/end-to-end/src/types/databases/trends/by-metric-trends-config';

export const CommonRevisionFlags = z.object({
  showKeyFactors: z.boolean().optional(),
  showGeolocation: z.boolean().optional(),
  showNewExplanations: z.boolean().optional(),
  pipelineAnalysisConfiguration: z
    .array(DynamicPipelineAnalysisConfiguration)
    .optional(),
  copilots: z.array(FullCopilotDefinition).optional(),
  showCustomChartTypes: z.boolean().optional(),
  showDashboardsPage: z.boolean().optional(),
  showRubricsPage: z.boolean().optional(),
  showExplorePage: z.boolean().optional(),
  showProgressPage: z.boolean().optional(),
  byMetricTrendsConfiguration: ByMetricTrendsConfig.optional(),
  enablePublicDashboards: z.boolean().optional(),
  allowInviteExternalUsersToDashboards: z.boolean().optional(),
  showDownloadMultiAnalysisButton: z.boolean().optional(),
  showDashboardAISummaries: z.boolean().optional(),
  showChatbot: z.boolean().optional(),
});

export type CommonRevisionFlags = z.infer<typeof CommonRevisionFlags>;

export const isBinScatterMultiSelectEnabledForRevision = ({}: {
  revisionFlags: CommonRevisionFlags | null;
}) => {
  // This is actually always enabled, so we just return true for now.
  return true;
};

export const isBinScatterCrossSelectEnabledForRevision = ({}: {
  revisionFlags: CommonRevisionFlags | null;
}) => {
  // This is always disabled.
  return false;
};

export const isPipelineAnalysisEnabledForRevision = ({
  analysisName,
  revisionFlags,
}: {
  analysisName: string | undefined;
  revisionFlags: CommonRevisionFlags | null;
}) => {
  const configuration = revisionFlags?.pipelineAnalysisConfiguration?.find(
    (c) => c.name === analysisName,
  );
  return Boolean(configuration);
};

export const isBiasAnalysisEnabledForRevision = ({}: {
  revisionFlags: CommonRevisionFlags | null;
}) => {
  // As a cost optimization, in the future we may want to return false here if the revision is in "intelligence only" mode.
  return true;
};

export const isPipelineAnalysisEnabledForAtLeastOneConfigForRevision = ({
  revisionFlags,
}: {
  revisionFlags: CommonRevisionFlags | null;
}) => {
  const configurations = revisionFlags?.pipelineAnalysisConfiguration ?? [];
  return configurations.length > 0;
};

export const isHiringCopilotEnabledForRevision = ({
  revisionFlags,
}: {
  revisionFlags: CommonRevisionFlags | null;
}) => {
  return Boolean(
    revisionFlags?.copilots?.some((c) => c.copilotType === 'hiring'),
  );
};

export const isPromotionCopilotEnabledForRevision = ({
  revisionFlags,
}: {
  revisionFlags: CommonRevisionFlags | null;
}) => {
  return Boolean(
    revisionFlags?.copilots?.some((c) => c.copilotType === 'promotion'),
  );
};

export const isPayBenchmarkingEnabledForRevision = ({
  revisionFlags,
}: {
  revisionFlags: CommonRevisionFlags | null;
}) => {
  return Boolean(
    revisionFlags?.copilots?.some((c) => c.copilotType === 'pay-benchmarking'),
  );
};

export const isPayCorrectionEnabledForRevision = ({
  revisionFlags,
}: {
  revisionFlags: CommonRevisionFlags | null;
}) => {
  return Boolean(
    revisionFlags?.copilots?.some((c) => c.copilotType === 'pay-correction'),
  );
};

export const isKeyFactorsEnabledForRevision = ({
  revisionFlags,
}: {
  revisionFlags: CommonRevisionFlags | null;
}) => {
  return Boolean(revisionFlags?.showKeyFactors);
};

export const isDashboardsEnabledForRevision = ({
  revisionFlags,
}: {
  revisionFlags: CommonRevisionFlags | null;
}) => {
  return Boolean(revisionFlags?.showDashboardsPage);
};

export const isRubricsPageEnabledForRevision = ({
  revisionFlags,
}: {
  revisionFlags: CommonRevisionFlags | null;
}) => {
  return Boolean(revisionFlags?.showRubricsPage);
};

export const isGeolocationEnabledForRevision = ({
  revisionFlags,
}: {
  revisionFlags: CommonRevisionFlags | null;
}) => {
  return Boolean(revisionFlags?.showGeolocation);
};

export const isCustomChartsEnabledForRevision = ({
  revisionFlags,
}: {
  revisionFlags: CommonRevisionFlags | null;
}) => {
  return (
    isDashboardsEnabledForRevision({ revisionFlags }) &&
    Boolean(revisionFlags?.showCustomChartTypes)
  );
};

export const isTableViewEnabledForRevision = ({
  revisionFlags,
}: {
  revisionFlags: CommonRevisionFlags | null;
}) => {
  return isCustomChartsEnabledForRevision({ revisionFlags });
};

export const isSummaryStatsEnabledForRevision = ({
  revisionFlags,
}: {
  revisionFlags: CommonRevisionFlags | null;
}) => {
  return isCustomChartsEnabledForRevision({ revisionFlags });
};

export const isRichTextEnabledForRevision = ({
  revisionFlags,
}: {
  revisionFlags: CommonRevisionFlags | null;
}) => {
  return isCustomChartsEnabledForRevision({ revisionFlags });
};

export const isNlpGraphEnabledForRevision = ({}: {
  revisionFlags: CommonRevisionFlags | null;
}) => {
  // Return true for now until there is a revision level feature flag.
  return true;
};

export const isHeatmapEnabledForRevision = ({
  revisionFlags,
}: {
  revisionFlags: CommonRevisionFlags | null;
}) => {
  return Boolean(revisionFlags?.showExplorePage);
};

export const isProgressEnabledForRevision = ({
  revisionFlags,
}: {
  revisionFlags: CommonRevisionFlags | null;
}) => {
  return Boolean(revisionFlags?.showProgressPage);
};

export const isTimeSeriesTrendsEnabledForRevision = ({
  revisionFlags,
}: {
  revisionFlags: CommonRevisionFlags | null;
}) => {
  return Boolean(revisionFlags?.byMetricTrendsConfiguration?.isEnabled);
};

export const isEventStudyEnabledForRevision = ({
  revisionFlags,
}: {
  revisionFlags: CommonRevisionFlags | null;
}) => {
  return Boolean(
    revisionFlags?.byMetricTrendsConfiguration?.isEnabled &&
      revisionFlags.byMetricTrendsConfiguration?.isEventStudyAnalysisEnabled,
  );
};

export const isCustomVizRequestEnabledForRevision = ({
  request,
  revisionFlags,
}: {
  request: CustomVizRequest;
  revisionFlags: CommonRevisionFlags | null;
}) => {
  if (request.customVizId === 'binScatterMultiSelect') {
    return isBinScatterMultiSelectEnabledForRevision({
      revisionFlags,
    });
  }

  if (request.customVizId === 'binScatterCrossSelect') {
    return isBinScatterCrossSelectEnabledForRevision({ revisionFlags });
  }

  if (request.customVizId === 'pipelineAnalysis') {
    return isPipelineAnalysisEnabledForRevision({
      revisionFlags,
      analysisName: request.inputs.extras.analysisName,
    });
  }

  if (request.customVizId === 'biasAnalysis') {
    return isBiasAnalysisEnabledForRevision({
      revisionFlags,
    });
  }

  if (request.customVizId === 'hiringCopilot') {
    return isHiringCopilotEnabledForRevision({
      revisionFlags,
    });
  }
  if (request.customVizId === 'promotionCopilot') {
    return isPromotionCopilotEnabledForRevision({
      revisionFlags,
    });
  }
  if (request.customVizId === 'payBenchmarking') {
    return isPayBenchmarkingEnabledForRevision({
      revisionFlags,
    });
  }
  if (request.customVizId === 'payCorrection') {
    return isPayCorrectionEnabledForRevision({
      revisionFlags,
    });
  }
  if (request.customVizId === 'successPhenotypeV2') {
    // This intentionally just verifies if either version of V2 (including key factors) is enabled.
    return isKeyFactorsEnabledForRevision({
      revisionFlags,
    });
  }

  if (request.customVizId === 'tableView') {
    return isTableViewEnabledForRevision({
      revisionFlags,
    });
  }

  if (request.customVizId === 'summaryStats') {
    return isSummaryStatsEnabledForRevision({
      revisionFlags,
    });
  }

  if (request.customVizId === 'richText') {
    return isRichTextEnabledForRevision({
      revisionFlags,
    });
  }

  if (request.customVizId === 'nlpGraph') {
    return isNlpGraphEnabledForRevision({
      revisionFlags,
    });
  }

  if (request.customVizId === 'geolocation') {
    return isGeolocationEnabledForRevision({
      revisionFlags,
    });
  }

  if (request.customVizId === 'heatmap') {
    return isHeatmapEnabledForRevision({
      revisionFlags,
    });
  }

  if (request.customVizId === 'progress') {
    return isProgressEnabledForRevision({
      revisionFlags,
    });
  }

  if (request.customVizId === 'timeSeriesTrends') {
    if (request.inputs.extras.treatmentDate) {
      return isEventStudyEnabledForRevision({
        revisionFlags,
      });
    }

    return isTimeSeriesTrendsEnabledForRevision({
      revisionFlags,
    });
  }

  if (request.customVizId === 'topLevelAnalysis') {
    return true;
  }

  if (request.customVizId === 'externalStreamlit') {
    return true;
  }

  throw guardExhaustive(request);
};

export const isPublishDashboardsEnabledForRevision = ({
  revisionFlags,
}: {
  revisionFlags: CommonRevisionFlags | null;
}) => {
  return Boolean(revisionFlags?.enablePublicDashboards);
};

export const isShowDashboardAISummariesEnabledForRevision = ({
  revisionFlags,
}: {
  revisionFlags: CommonRevisionFlags | null;
}) => {
  return Boolean(revisionFlags?.showDashboardAISummaries);
};

export const isInviteExternalUsersToDashboardsEnabledForRevision = ({
  revisionFlags,
}: {
  revisionFlags: CommonRevisionFlags | null;
}) => {
  return Boolean(revisionFlags?.allowInviteExternalUsersToDashboards);
};

export const isDashboardShareButtonEnabledForRevision = ({
  revisionFlags,
}: {
  revisionFlags: CommonRevisionFlags | null;
}) => {
  return Boolean(
    revisionFlags?.enablePublicDashboards ||
      revisionFlags?.allowInviteExternalUsersToDashboards ||
      revisionFlags?.showDownloadMultiAnalysisButton,
  );
};

export const isShowChatbotEnabledForRevision = ({
  revisionFlags,
}: {
  revisionFlags: CommonRevisionFlags | null;
}) => Boolean(revisionFlags?.showChatbot);
